import * as React from "react";
import styled from "styled-components";
import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import SelectedDataLakesContext from "data-lake/contexts/SelectedDataLakesContext";
import { DropdownButton, MenuItem, DeleteMenuItem, ButtonToolbar } from "components/bootstrap";
import PreviewExternalLakeLogsButton from "data-lake/components/PreviewExternalLakeLogsButton";
import RetrievalArchiveButton from "data-lake/data-lake-overview/actions/RetrievalArchiveButton";
import RetrievalArchiveModal from "data-lake/data-lake-overview/actions/RetrievalArchiveModal";
import { EXTERNAL_DATA_LAKE_STORAGE_TYPE_ATHENA } from "data-lake/preview/storage-types/external/Constants";
import { EXTERNAL_DATA_LAKE_TYPE } from "data-lake/Constants";
import { IfPermitted, ShareMenuItem } from "components/common";
import EntityShareModal from "components/permissions/EntityShareModal";
import useStream from "components/streams/hooks/useStream";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useRetrieval from "data-lake/preview/hooks/useRetrieval";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { ExternalDataLakeInputType } from "./DataLakeConnectorCreateInputModal";
const StyledButtonToolbar = styled(ButtonToolbar)`
  justify-content: flex-end;
`;
const DataLakeConnectorActions = ({ onEdit, onDelete, onCreateInput, connector }) => {
  var _a;
  const { t } = useTranslation("enterprise_data_lake");
  const { id: dataLakeId, data_lake_name: dataLakeTitle, stream_id: streamId } = connector;
  const [showRetrievalModal, setShowRetrievalModal] = useState(false);
  const [showEntityShareModal, setShowEntityShareModal] = useState(false);
  const toggleEntityShareModal = useCallback(() => setShowEntityShareModal((cur) => !cur), []);
  const toggleRetrievalModal = useCallback(() => setShowRetrievalModal((cur) => !cur), []);
  const selectedDataLake = useMemo(
    () => ({
      selectedDataLake: {
        id: dataLakeId,
        storageType: EXTERNAL_DATA_LAKE_STORAGE_TYPE_ATHENA,
        type: EXTERNAL_DATA_LAKE_TYPE,
        active: true,
        title: dataLakeTitle
      }
    }),
    [dataLakeId, dataLakeTitle]
  );
  const { data: stream } = useStream(streamId, { refetchInterval: false });
  const sendTelemetry = useSendTelemetry();
  const { onRetrieveMessages } = useRetrieval();
  const handlePreviewLogsClick = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.PREVIEW_LOGS_BUTTON_CLICKED, {});
  }, [sendTelemetry]);
  const handleRetrieveLogsClick = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.RETRIEVE_LOGS_BUTTON_CLICKED, {});
    toggleRetrievalModal();
  }, [sendTelemetry, toggleRetrievalModal]);
  const handleEdit = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.EDIT_CONNECTOR_CLICKED, {});
    onEdit();
  }, [onEdit, sendTelemetry]);
  const handleCreateInput = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.CREATE_INPUT_CLICKED, {});
    onCreateInput();
  }, [onCreateInput, sendTelemetry]);
  const handleDelete = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.DELETE_CONNECTOR_CLICKED, {});
    onDelete();
  }, [onDelete, sendTelemetry]);
  if (!dataLakeId) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(StyledButtonToolbar, null, /* @__PURE__ */ React.createElement(PreviewExternalLakeLogsButton, { dataLakeId, onClick: handlePreviewLogsClick }), /* @__PURE__ */ React.createElement(
    RetrievalArchiveButton,
    {
      onClick: handleRetrieveLogsClick,
      buttonType: "button",
      dataLakeType: EXTERNAL_DATA_LAKE_TYPE,
      dataLakeStorageType: EXTERNAL_DATA_LAKE_STORAGE_TYPE_ATHENA
    }
  ), /* @__PURE__ */ React.createElement(DropdownButton, { bsSize: "xs", title: t("connector_actions.more"), pullRight: true }, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: handleEdit }, t("connector_actions.edit")), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`inputs:create:${ExternalDataLakeInputType}`] }, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: handleCreateInput }, t("connector_actions.create_input"))), /* @__PURE__ */ React.createElement(
    ShareMenuItem,
    {
      entityId: streamId,
      entityType: "stream",
      onClick: toggleEntityShareModal,
      title: t("connector_actions.share_stream")
    }
  ), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["data_lake_archive:delete"] }, /* @__PURE__ */ React.createElement(DeleteMenuItem, { onSelect: handleDelete }, t("connector_actions.delete")))), showRetrievalModal && /* @__PURE__ */ React.createElement(SelectedDataLakesContext.Provider, { value: selectedDataLake }, /* @__PURE__ */ React.createElement(
    RetrievalArchiveModal,
    {
      onToggleModal: toggleRetrievalModal,
      calculateEstimatedSize: false,
      onRetrieveMessages
    }
  )), showEntityShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: streamId,
      entityType: "stream",
      entityTitle: (_a = stream == null ? void 0 : stream.title) != null ? _a : dataLakeTitle,
      description: t("connector_actions.share_description"),
      onClose: toggleEntityShareModal
    }
  ));
};
export default DataLakeConnectorActions;
